import ContentDialog from '@shared/components/core/dialog/ContentDialog';
import TextFieldWithValidation from '@shared/components/core/field/TextFieldWithValidation';
import { OrgDetailWrapper } from '@shared/components/organization/CommonOrgStyles';
import RoleEditor from '@shared/components/organization/role/RoleEditor';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import InviteOrganizationMemberRequest from '@shared/models/organizations/InviteOrganizationMemberRequest';
import OrganizationMemberRole from '@shared/models/organizations/OrganizationMemberRole';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IInviteCreateDialogProps {
    open: boolean;
    onClose: () => void;
    myRole: OrganizationMemberRole;
    organizationId: string;
}

export default function InviteCreateDialog({
    open,
    onClose,
    myRole,
    organizationId,
}: Readonly<IInviteCreateDialogProps>) {
    const { t } = useTranslation();

    const newMemberRole: OrganizationMemberRole = {
        isAdmin: false,
        canViewMembers: false,
        canInviteMembers: false,
        canChangeMemberPermissions: false,
        canRemoveMembers: false,
        canCreateAuthClients: false,
        canViewAuthClients: false,
        canManageBilling: false,
        canViewAnalytics: false,
        canManageChildOrganizations: false,
        canAccessOrganizationProfiles: false,
        canEditOrganizationProfiles: false,
        canManageGlobalAutomationRules: false,
    };
    const initialInvite: InviteOrganizationMemberRequest = {
        email: '',
        ...newMemberRole,
    };
    const [invite, setInvite] = useState<InviteOrganizationMemberRequest>(initialInvite);

    const [role, setRole] = useState<OrganizationMemberRole>(newMemberRole);
    const [emailValidation, setEmailValidation] = useState<string | null>(null);

    useEffect(() => {
        if (invite?.email) setEmailValidation(null);
    }, [invite]);

    const handleRoleChange = (updatedRole: OrganizationMemberRole) => {
        const newRole = { ...updatedRole };
        if (newRole.isAdmin) {
            for (const key in updatedRole) {
                if (Object.prototype.hasOwnProperty.call(updatedRole, key)) {
                    const typedKey = key as keyof OrganizationMemberRole;
                    newRole[typedKey] = true;
                }
            }
        }
        setRole(newRole);
        setInvite({
            ...invite,
            ...newRole,
        });
    };

    const handleInviteChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setInvite({
            ...invite,
            email: value,
        });
    };

    const handleSave = async () => {
        if (!invite.email) {
            setEmailValidation(t('common:organization.invite.emailRequired'));
            return;
        }
        if (invite.email && !invite.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setEmailValidation(t('common:organization.invite.emailInvalid'));
            return;
        }

        const thisOrg = useOrganizationStore.getState().invites?.find((org) => org.organizationId === organizationId);
        if (
            thisOrg &&
            thisOrg.invites?.some(
                (existingInvite) =>
                    existingInvite.email === invite.email && new Date(existingInvite.expirationDate) > new Date()
            )
        ) {
            // invite already exists

            setEmailValidation(t('common:organization.invite.emailExists'));
            return;
        }

        const membersResult = await useOrganizationStore.getState().loadOrganizationMembers(organizationId);
        if (membersResult.resultType === 'Ok' && membersResult.data) {
            const existingMember = membersResult.data.find((member) => member.user.email === invite.email);
            if (existingMember) {
                // member already exists
                setEmailValidation(t('common:organization.member.emailExists'));
                return;
            }
        }

        const result = await useOrganizationStore.getState().createOrganizationInvite(organizationId, invite);
        if (result?.resultType == 'Ok') {
            useAlertsStore
                .getState()
                .showSuccess(t('common:organization.invite.send.success', 'Success', { value: invite.email }));
        } else {
            // console.log(result);
            useAlertsStore
                .getState()
                .showError(t('common:organization.invite.send.error', 'Error with invite', { value: invite.email }));
        }
        onClose();
        setRole({ ...newMemberRole });
        setInvite({ ...initialInvite });
    };

    if (!organizationId || !myRole) return null;

    return (
        <ContentDialog
            fullWidth
            open={open}
            onClose={onClose}
            onCancel={onClose}
            title={t('common:organization.invite.edit')}
            description={t('common:organization.invite.editDescription')}
            confirmButtonText={t('common:organization.invite.send.title')}
            onConfirm={handleSave}>
            <OrgDetailWrapper>
                <TextFieldWithValidation
                    required={true}
                    label="Email"
                    value={invite?.email}
                    placeholder={'email@email.com'}
                    hasError={!!emailValidation}
                    helperTextMessageOnError={emailValidation}
                    onChange={handleInviteChange}
                />
            </OrgDetailWrapper>
            <RoleEditor
                title={t('common:organization.role.title')}
                myRole={myRole}
                role={role}
                onChange={handleRoleChange}
            />
        </ContentDialog>
    );
}
